import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Chip, Snackbar, Alert, Tab, Tabs, useMediaQuery, useTheme } from '@mui/material';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Badge from '@/components/ui/badge/Badge';
import { QcTaskEllipsisText } from '../../../my-task/mt-qc-task-001/components/table/QcTaskTableCellRenderers';
import { formatQcTaskCellValue } from '../../../my-task/mt-qc-task-001/components/table/qcTaskTableFormatters';
import SegmentationTool, { SegAnnotTextView } from '@/components/SegmentationTool';
import { SEGMENTATION_LABELS, ANNOTATION_LABELS } from '@/components/WaveformPlayer/SegmentationPlayer';
import {
  submitSegments,
  submitAnnotations,
  saveSegmentsLater,
  getSegments,
} from '@/api/segmentationApi';
import { ANNOT_SEG_TABS } from '../hooks/useAnnotSegPageController';

const TOOLBAR_TAB_ITEMS = [
  { value: ANNOT_SEG_TABS.NEW, label: 'New' },
  { value: ANNOT_SEG_TABS.SAVE_FOR_LATER, label: 'Save for Later' },
  { value: ANNOT_SEG_TABS.SUBMITTED, label: 'Submitted' },
];

function getAnnotSegColumns(onOpenRow, onOpenViewRow) {
  return [
    {
      id: 'annotSegSerial',
      header: 'S.No',
      size: 65,
      minSize: 40,
      enableSorting: false,
      enableColumnActions: false,
      Cell: ({ row, table }) => {
        const { pageIndex, pageSize } = table.getState().pagination;
        return <QcTaskEllipsisText>{pageIndex * pageSize + row.index + 1}</QcTaskEllipsisText>;
      },
    },
    {
      id: 'annotSegAction',
      header: 'Action',
      size: 95,
      minSize: 60,
      maxSize: 110,
      enableSorting: false,
      enableColumnActions: false,
      enableClickToCopy: false,
      Cell: ({ row }) => {
        const editable =
          row.original.req_status === 'OPEN' || row.original.req_status === 'IN-PROGRESS';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
            {editable ? (
              <Badge
                variant="light"
                color="success"
                size="sm"
                onClick={() => onOpenRow?.(row.original)}
                aria-label="Open task"
                className="shrink-0 scale-90 sm:scale-95 md:scale-100"
              >
                Open
              </Badge>
            ) : (
              <Badge
                variant="light"
                color="info"
                size="sm"
                onClick={() => onOpenViewRow?.(row.original)}
                aria-label="View task"
                className="shrink-0 scale-90 sm:scale-95 md:scale-100"
              >
                View
              </Badge>
            )}
          </Box>
        );
      },
    },
    {
      accessorKey: 'file_name',
      header: 'File Name',
      size: 210,
      minSize: 130,
      maxSize: 320,
      Cell: ({ cell }) => (
        <QcTaskEllipsisText>{formatQcTaskCellValue(cell.getValue())}</QcTaskEllipsisText>
      ),
    },
    {
      accessorKey: 'language',
      header: 'Language',
      size: 150,
      minSize: 110,
      maxSize: 180,
      Cell: ({ cell }) => (
        <QcTaskEllipsisText>{formatQcTaskCellValue(cell.getValue())}</QcTaskEllipsisText>
      ),
    },
    {
      accessorKey: 'state',
      header: 'State',
      size: 150,
      minSize: 110,
      maxSize: 180,
      Cell: ({ cell }) => (
        <QcTaskEllipsisText>{formatQcTaskCellValue(cell.getValue())}</QcTaskEllipsisText>
      ),
    },
    {
      accessorKey: 'district',
      header: 'District',
      size: 150,
      minSize: 110,
      maxSize: 180,
      Cell: ({ cell }) => (
        <QcTaskEllipsisText>{formatQcTaskCellValue(cell.getValue())}</QcTaskEllipsisText>
      ),
    },
    {
      accessorKey: 'req_status',
      header: 'Status',
      size: 140,
      minSize: 110,
      maxSize: 180,
      enableSorting: false,
      Cell: ({ cell }) => {
        const v = cell.getValue();
        const color = v === 'OPEN' ? 'warning' : v === 'SUBMITTED' ? 'success' : 'info';
        return <Chip size="small" label={v ?? '—'} color={color} variant="outlined" />;
      },
    },
    {
      id: 'assigned_to',
      header: 'Assignee',
      size: 190,
      minSize: 130,
      maxSize: 280,
      enableSorting: false,
      accessorFn: (row) => row?.assigned_to?.name ?? row?.assigned_to?.email ?? '',
      Cell: ({ cell }) => (
        <QcTaskEllipsisText>{formatQcTaskCellValue(cell.getValue())}</QcTaskEllipsisText>
      ),
    },
    {
      accessorKey: 'assignment_id',
      header: 'Assignment ID',
      size: 170,
      minSize: 130,
      maxSize: 220,
      Cell: ({ cell }) => (
        <QcTaskEllipsisText>{formatQcTaskCellValue(cell.getValue())}</QcTaskEllipsisText>
      ),
    },
  ];
}

function AnnotSegTableComponent({
  data,
  rowCount,
  isLoading,
  activeTab,
  onTabChange,
  pagination,
  onPaginationChange,
  onRefreshTable,
  pageSizeOptions,
  defaultPageSize,
  tableFilters,
  onTableFiltersChange,
  sorting,
  onSortingChange,
  isAnnotation,
  tagOptions,
  workflowId,
}) {
  const [activeRow, setActiveRow] = useState(null);
  const [activeRowIndex, setActiveRowIndex] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewRow, setViewRow] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [localColumnFilters, setLocalColumnFilters] = useState([]);
  const [localGlobalFilter, setLocalGlobalFilter] = useState('');

  const theme = useTheme();
  const isBelowMd = useMediaQuery(theme.breakpoints.down('md'));
  const isBelowSm = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();

  const showToast = useCallback((message, severity = 'success') => {
    setToast({ open: true, message, severity });
  }, []);

  const { data: savedSegmentsData } = useQuery({
    queryKey: ['annotSegSegments', activeRow?.assignment_id],
    queryFn: () => getSegments({ assignmentId: activeRow.assignment_id }),
    enabled: dialogOpen && !!activeRow?.assignment_id,
    staleTime: Infinity,
  });

  const initialSegments = useMemo(() => {
    const raw = savedSegmentsData?.data ?? savedSegmentsData ?? [];
    return Array.isArray(raw) ? raw : (raw?.segments ?? []);
  }, [savedSegmentsData]);

  const savedTranscript = useMemo(() => {
    const raw = savedSegmentsData?.data ?? savedSegmentsData ?? null;
    if (!raw || Array.isArray(raw)) return null;
    return raw.updated_vendor_transcript ?? null;
  }, [savedSegmentsData]);

  const submitMutation = useMutation({
    mutationFn: ({ assignmentId, segments, transcript, comment }) =>
      isAnnotation
        ? submitAnnotations({ assignmentId, segments, transcript, comment })
        : submitSegments({ assignmentId, segments, transcript, comment }),
    onError: (err) =>
      showToast(err?.response?.data?.message ?? err?.message ?? 'Submission failed', 'error'),
  });

  const saveLaterMutation = useMutation({
    mutationFn: saveSegmentsLater,
    onError: (err) =>
      showToast(err?.response?.data?.message ?? err?.message ?? 'Save failed', 'error'),
  });

  const isSubmitting = submitMutation.isPending || saveLaterMutation.isPending;

  const handleOpenRow = useCallback((row) => {
    const idx = Array.isArray(data) ? data.findIndex((r) => r.assignment_id === row.assignment_id) : -1;
    setActiveRow(row);
    setActiveRowIndex(idx);
    setDialogOpen(true);
  }, [data]);

  const handleOpenViewRow = useCallback((row) => {
    setViewRow(row);
    setViewDialogOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setDialogOpen(false);
    setActiveRow(null);
    setActiveRowIndex(null);
  }, []);

  const showNextRow = useCallback(() => {
    const tasks = Array.isArray(data) ? data : [];
    const nextIndex = (activeRowIndex ?? 0) + 1;
    if (nextIndex >= tasks.length) {
      handleClose();
      return;
    }
    setActiveRow(tasks[nextIndex]);
    setActiveRowIndex(nextIndex);
  }, [activeRowIndex, data, handleClose]);

  const handleSaveLater = useCallback(
    (segments, transcript, comment) => {
      if (!activeRow) return;
      saveLaterMutation.mutate(
        { assignmentId: activeRow.assignment_id, segments, transcript, comment },
        {
          onSuccess: () => {
            showToast('Saved for later', 'success');
            queryClient.invalidateQueries({ queryKey: ['annotSegTasks'] });
            showNextRow();
          },
        },
      );
    },
    [activeRow, saveLaterMutation, showNextRow, queryClient, showToast],
  );

  const handleSubmitNext = useCallback(
    (segments, transcript, comment) => {
      if (!activeRow) return;
      submitMutation.mutate(
        { assignmentId: activeRow.assignment_id, segments, transcript, comment },
        {
          onSuccess: () => {
            showToast('Submitted', 'success');
            queryClient.invalidateQueries({ queryKey: ['annotSegTasks'] });
            showNextRow();
          },
        },
      );
    },
    [activeRow, submitMutation, showNextRow, queryClient, showToast],
  );

  const handleSubmitClose = useCallback(
    (segments, transcript, comment) => {
      if (!activeRow) return;
      submitMutation.mutate(
        { assignmentId: activeRow.assignment_id, segments, transcript, comment },
        {
          onSuccess: () => {
            showToast('Submitted', 'success');
            queryClient.invalidateQueries({ queryKey: ['annotSegTasks'] });
            handleClose();
          },
        },
      );
    },
    [activeRow, submitMutation, handleClose, queryClient, showToast],
  );

  const renderTopToolbarCustomActions = useCallback(
    () => (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: { xs: '100%', md: 'auto' },
          minWidth: 0,
          mr: { xs: 0, md: 1 },
          mb: { xs: 0.25, md: 0 },
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, value) => onTabChange?.(value)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            width: { xs: '100%', md: 'auto' },
            minHeight: 'unset',
            '& .MuiTabs-scrollButtons': {
              width: { xs: 28, sm: 32 },
              '&.Mui-disabled': { opacity: 0.35 },
            },
            '& .MuiTabs-flexContainer': { gap: { xs: 0, sm: 0.5 } },
            '& .MuiTab-root': {
              minHeight: { xs: 34, sm: 38, md: 36 },
              py: { xs: 0.35, sm: 0.5, md: 0 },
              px: { xs: 0.75, sm: 1 },
              fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' },
              textTransform: 'none',
            },
          }}
        >
          {TOOLBAR_TAB_ITEMS.map(({ value, label }) => (
            <Tab key={value} value={value} label={label} />
          ))}
        </Tabs>
      </Box>
    ),
    [activeTab, onTabChange],
  );

  const columns = useMemo(
    () => getAnnotSegColumns(handleOpenRow, handleOpenViewRow),
    [handleOpenRow, handleOpenViewRow],
  );

  const mrtColumnFilters = useMemo(
    () =>
      Object.entries(tableFilters ?? {})
        // `search` maps to MRT's separate global-filter concept, not a per-column filter.
        .filter(([id]) => id !== 'search')
        .map(([id, value]) => ({ id, value }))
        .filter(({ value }) => value != null && String(value).trim() !== '')
        .map(({ id, value }) => ({ id, value: String(value) })),
    [tableFilters],
  );

  useEffect(() => {
    setLocalColumnFilters(mrtColumnFilters);
  }, [mrtColumnFilters]);

  useEffect(() => {
    setLocalGlobalFilter(tableFilters?.search ?? '');
  }, [tableFilters]);

  const filterDebounceTimerRef = useRef(null);
  useEffect(() => () => {
    if (filterDebounceTimerRef.current) clearTimeout(filterDebounceTimerRef.current);
  }, []);

  /**
   * Shared by onColumnFiltersChange / onGlobalFilterChange: builds the combined filter
   * object (column filters + search) and pushes it to the URL/API, debounced. Both column
   * filters and the search box write here so neither one clobbers the other's active value.
   */
  const pushFilters = useCallback(
    (columnFiltersList, searchValue) => {
      const nextFilters = {};
      (columnFiltersList ?? []).forEach((f) => {
        if (!f?.id) return;
        const s = f?.value == null ? '' : String(f.value).trim();
        if (s) nextFilters[f.id] = s;
      });
      const search = searchValue == null ? '' : String(searchValue).trim();
      if (search) nextFilters.search = search;

      if (typeof onPaginationChange === 'function') {
        onPaginationChange((prev) => ({ ...prev, pageIndex: 0 }));
      }

      if (filterDebounceTimerRef.current) clearTimeout(filterDebounceTimerRef.current);
      filterDebounceTimerRef.current = setTimeout(() => {
        try {
          onTableFiltersChange?.(nextFilters);
        } catch (err) {
          if (import.meta.env.DEV) {
            // eslint-disable-next-line no-console
            console.warn('[AnnotSegTable] onTableFiltersChange threw', err);
          }
        }
      }, 400);
    },
    [onPaginationChange, onTableFiltersChange],
  );

  const tableOptions = useMemo(
    () => ({
      columns,
      data,
      enableRowVirtualization: true,
      enableStickyHeader: true,
      enableColumnActions: true,
      enableGlobalFilter: true,
      enableColumnFilters: true,
      enableFilters: true,
      columnFilterDisplayMode: isBelowMd ? 'popover' : 'subheader',
      enableHiding: true,
      enableSorting: true,
      enableColumnOrdering: true,
      enableColumnResizing: true,
      enableDensityToggle: true,
      enableFullScreenToggle: true,
      enablePagination: true,
      layoutMode: 'grid',
      memoMode: 'cells',
      enableColumnPinning: true,
      manualPagination: true,
      manualFiltering: true,
      manualSorting: true,
      enableClickToCopy: true,
      rowCount: rowCount ?? 0,
      state: {
        isLoading: Boolean(isLoading),
        pagination: pagination ?? { pageIndex: 0, pageSize: defaultPageSize ?? 20 },
        columnFilters: localColumnFilters,
        globalFilter: localGlobalFilter,
        sorting: sorting ?? [],
      },
      onPaginationChange,
      onSortingChange,
      onColumnFiltersChange: (updater) => {
        const next = typeof updater === 'function' ? updater(localColumnFilters) : updater;
        setLocalColumnFilters(next ?? []);
        pushFilters(next, localGlobalFilter);
      },
      onGlobalFilterChange: (updater) => {
        const next = typeof updater === 'function' ? updater(localGlobalFilter) : updater;
        const s = next == null ? '' : String(next);
        setLocalGlobalFilter(s);
        pushFilters(localColumnFilters, s);
      },
      initialState: {
        density: 'compact',
        pagination: { pageIndex: 0, pageSize: defaultPageSize ?? 20 },
        columnPinning: { left: ['annotSegSerial', 'annotSegAction'] },
      },
      muiTableContainerProps: {
        sx: {
          width: '100%',
          height: {
            xs: 'min(max(220px, 44dvh), 460px)',
            sm: 'min(max(280px, 48dvh), 560px)',
            md: 720,
            lg: 780,
            xl: 820,
          },
          maxHeight: {
            xs: 'min(max(220px, 44dvh), 460px)',
            sm: 'min(max(280px, 48dvh), 560px)',
            md: 720,
            lg: 780,
            xl: 820,
          },
          overflowY: 'auto',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          touchAction: { xs: 'pan-x pan-y', md: 'auto' },
          overscrollBehaviorX: { xs: 'contain', md: 'auto' },
        },
      },
      muiTablePaperProps: ({ table }) => ({
        sx: {
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden',
          borderRadius: { xs: 1, sm: 1.25, md: 2 },
          bgcolor: 'background.paper',
        },
        style: table.getState().isFullScreen ? { zIndex: 1_000_000 } : {},
      }),
      muiTableProps: {
        sx: {
          minWidth: 'max-content',
        },
      },
      muiTableHeadCellProps: {
        sx: {
          fontSize: { xs: '0.625rem', sm: '0.6875rem', md: '0.75rem' },
          fontWeight: 600,
          py: { xs: 0.35, sm: 0.45, md: 0.6 },
          px: { xs: 0.35, sm: 0.5, md: 0.75 },
          lineHeight: 1.2,
          whiteSpace: 'nowrap',
          verticalAlign: 'middle',
        },
      },
      muiTableBodyCellProps: {
        sx: {
          fontSize: { xs: '0.6875rem', sm: '0.75rem', md: '0.8125rem' },
          py: { xs: 0.3, sm: 0.4, md: 0.5 },
          px: { xs: 0.35, sm: 0.5, md: 0.75 },
          lineHeight: 1.25,
          verticalAlign: 'middle',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
      },
      positionGlobalFilter: isBelowMd ? 'left' : 'right',
      muiSearchTextFieldProps: {
        placeholder: 'Search…',
        size: 'small',
        sx: {
          flex: { xs: '1 1 100%', sm: '1 1 auto' },
          minWidth: { xs: 0, sm: 200 },
          maxWidth: { xs: '100%', sm: 360 },
          '& .MuiOutlinedInput-root': {
            minHeight: { xs: 34, sm: 36, md: 40 },
            fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' },
          },
        },
      },
      muiTopToolbarProps: {
        sx: {
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          gap: { xs: 0.5, sm: 0.75, md: 0.5 },
          py: { xs: 0.5, sm: 0.65, md: 0.5 },
          px: { xs: 0.25, sm: 0.5, md: 0 },
          flexWrap: { xs: 'nowrap', md: 'wrap' },
          maxWidth: '100%',
          overflowX: { xs: 'auto', md: 'hidden' },
          overflowY: { xs: 'visible', md: 'hidden' },
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: { xs: 'thin', md: undefined },
          '& .MuiIconButton-root': { padding: { xs: 0.35, sm: 0.5, md: 0.65 } },
        },
      },
      muiBottomToolbarProps: {
        sx: {
          flexWrap: 'wrap',
          justifyContent: { xs: 'center', sm: 'flex-end' },
          gap: { xs: 0.5, sm: 0.5 },
          py: { xs: 0.5, sm: 0.65 },
          px: { xs: 0.25, sm: 1 },
          fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' },
        },
      },
      renderTopToolbarCustomActions,
      muiPaginationProps: {
        rowsPerPageOptions: pageSizeOptions ?? [10, 20, 50, 100],
        showFirstButton: !isBelowSm,
        showLastButton: !isBelowSm,
        SelectProps: {
          MenuProps: {
            disableScrollLock: true,
            PaperProps: { sx: { maxHeight: { xs: '45dvh', sm: '50vh' }, overflowY: 'auto' } },
          },
        },
      },
    }),
    [
      columns, data, defaultPageSize, isBelowMd, isBelowSm, isLoading,
      localColumnFilters, localGlobalFilter, pushFilters, onPaginationChange,
      pageSizeOptions, pagination, renderTopToolbarCustomActions,
      rowCount, sorting, onSortingChange,
    ],
  );

  const table = useMaterialReactTable(tableOptions);

  const toolLabels = isAnnotation ? ANNOTATION_LABELS : SEGMENTATION_LABELS;
  const toolTitle = isAnnotation ? 'Annotate Audio' : 'Segment Audio';

  return (
    <>
      <MaterialReactTable table={table} />

      <SegmentationTool
        key={activeRow?.assignment_id ?? 'none'}
        open={dialogOpen}
        onClose={handleClose}
        audioFileURL={activeRow?.file_path}
        fileName={activeRow?.file_name}
        state={activeRow?.state}
        district={activeRow?.district}
        language={activeRow?.language}
        assignedToName={activeRow?.assigned_to?.name ?? activeRow?.assigned_to?.email}
        onSaveLater={handleSaveLater}
        onSubmitNext={handleSubmitNext}
        onSubmitClose={handleSubmitClose}
        initialSegments={initialSegments}
        transcription={activeRow?.vendor_transcript ?? ''}
        transcription2={activeRow?.vendor_transcript_2 ?? ''}
        updatedTranscription={savedTranscript ?? ''}
        workflowId={workflowId}
        submitting={isSubmitting}
        labels={toolLabels}
        tagOptions={isAnnotation ? tagOptions : undefined}
        showLabels={false}
        title={toolTitle}
      />

      <SegAnnotTextView
        key={viewRow?.assignment_id ?? 'view-none'}
        open={viewDialogOpen}
        onClose={() => { setViewDialogOpen(false); setViewRow(null); }}
        row={viewRow}
        workflowType={isAnnotation ? 'ANNOTATION' : 'SEGMENTATION'}
        workflowId={workflowId}
        assignedStatus={viewRow?.req_status ?? 'SUBMITTED'}
        assignedToName={viewRow?.assigned_to?.name ?? viewRow?.assigned_to?.email}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))}>
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
}

const AnnotSegTable = memo(AnnotSegTableComponent);
export default AnnotSegTable;
